import { Injectable } from '@angular/core';
import { INDRABookedTripSummary } from '@ibe.models/booked-trips.indra.model.lib';
import {TRIP_TYPE_ROUND_TRIP_CODE, TRIP_TYPE_ONE_WAY_CODE, TRIP_TYPE_MULTICITY_CODE, PAX_TYPE_ADULT_CODE, PAX_TYPE_CHILD_CODE, PAX_TYPE_INFANT_CODE, PAX_TYPE_YOUNG_ADULT_CODE, DEFAULT_LANGUAGE} from '@ibe.config';
import { DomSanitizer } from '@angular/platform-browser';
import { INDRAPnrDataRecon } from '@ibe.models/pnr-data.indra.model.lib';
import { INDRADateService } from '@ibe.services/date.indra.service';
import { INDRAIbeAdminService } from '@ibe.services/ibeadmin.indra.service';
import {INDRABFMDataStorageService} from '@ibe.services/bfm-data-storage.indra.service';

@Injectable({
  providedIn: 'root'
})
export class INDRAMybService {


	constructor(private sanitizer: DomSanitizer,
		private dateService: INDRADateService,
		private ibeAdminService: INDRAIbeAdminService,
		private storage: INDRABFMDataStorageService) {
	}

	/**
	 * Get booking type text formated
	 * @param {INDRABookedTripSummary} booked_trip to return
	 * @returns booking type text
	 */
	INDRA_getBookingTypeForPNRSummary(booked_trip: INDRABookedTripSummary): string {
		if (booked_trip.multiCity) {
			return this.INDRA_getTranslation('bookingDetails.bookingType.multicity');
		} else if (booked_trip.roundTrip) {
			return this.INDRA_getTranslation('bookingDetails.bookingType.roundTrip');
		} else if (booked_trip.oneWay) {
			return this.INDRA_getTranslation('bookingDetails.bookingType.oneWay');
		}
	}

	/**
	 * Get booking type icon
	  * @param {INDRABookedTripSummary} booked_trip to return
	 * @returns booking type text
	 */
	INDRA_getBookingTypeIconForPNRSummary(booked_trip: INDRABookedTripSummary): string {
		if (booked_trip.multiCity) {
			return 'icons.svg#plane_related--world_tracker';
		} else if (booked_trip.roundTrip) {
			return 'icons.svg#plane_related--ida-volta';
		} else if (booked_trip.oneWay) {
			return 'icons.svg#plane_related--outbound_flight';
		} else {
			return 'icons.svg#plane_related--flights';
		}
	}

	/**
	 * Get number of days left from today until a date
	 * @param {string} date to compare with today
	 * @returns number of days left to date
	 */
	INDRA_getTimeLeft(date: string): string {
		const diffInSecs = this.dateService.INDRA_getRemainingTimeInSeconds(date);
		if (diffInSecs >= 0 ) {
			const oneDayTime = 24 * 60 * 60; // hours*minutes*seconds
			const timeLeft =
				Math.round(
					Math.abs(diffInSecs / oneDayTime)
				);

			if (timeLeft > 0) {
				return 'In ' + timeLeft + ' ' + this.INDRA_getTranslation('bookingDetails.label.days');
			} else {
				return 'Today';
			}

		}
	}

	/**
	 * Get destination banner image from URL
	 * @param destination Name to use on URL
	 * @returns sanitized background-image style
	 */
	INDRA_printDestinationImage(destination: String) {
		let url;
		if (destination.includes('Multicity')) {
			url =
			'url(./assets/images/destinations/banner/' +
			destination.toUpperCase() +
			'.jpg)';
		} else {
			url =
			'url(./assets/images/destinations/banner/' +
			destination.toUpperCase() +
			'.jpg)';
		}
		const destinationImageUrl = this.sanitizer.bypassSecurityTrustStyle(url);
		return destinationImageUrl;
	}

	/**
	 * Get destination logo in SVG image from URL
	 * @param destination Name to use on URL
	 * @returns sanitized logo url
	 */
	INDRA_printDestinationLogo(destination: String) {
		const lang = (sessionStorage.getItem('language') ? sessionStorage.getItem('language') : DEFAULT_LANGUAGE);
		const url =
			'./assets/images/destinations/logo/' + destination.toUpperCase() + '/' + lang.toUpperCase() + '.svg';
		const destinationLogoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
			url
		);
		return destinationLogoUrl;
	}

	/**
	 * Get booking dates text formated
	 * @param INDRABookedTripSummary to get and return dates
	 * @returns booking dates text formated
	 */
	INDRA_getBfmFlightDatesForPNRSummary(pnrSummary: INDRABookedTripSummary): string {
		let returnValue;
		if (!pnrSummary.origin) {
			returnValue = '----';
		}

		let sameDateFlag = this.dateService.INDRA_isSameDay(
			pnrSummary.departureDate.toString(),
			pnrSummary.arrivalDate.toString());

		if (pnrSummary.oneWay || sameDateFlag) {
			// Oneway or if departure and arrival date fall on the same day (IBETPIUNO-6313).
			returnValue = this.INDRA_formatDate(pnrSummary.departureDate.toString());
		} else {
			let departureDate;
			if (
					// If departure and return on same month
					this.dateService.INDRA_getMonth(pnrSummary.departureDate.toString()) ===
					this.dateService.INDRA_getMonth(pnrSummary.arrivalDate.toString())
				) {
					departureDate = this.dateService.INDRA_formatDate(pnrSummary.departureDate.toString(), 'DD');
				} else {
					// If departure and return on same year and different month
					departureDate = this.dateService.INDRA_formatDate(pnrSummary.departureDate.toString(), 'DD MMM');
				}
			return (
				departureDate +
				' - ' +
				this.INDRA_formatDate(pnrSummary.arrivalDate.toString())
			);
		}
		return returnValue;
	}

	INDRA_formatDate(date: string): string {
		return this.dateService.INDRA_formatDate(date, 'DD MMM YYYY');
	}

	/**
	 * Get number of trip days
	 * @param INDRABookedTripSummary to get number of trip days
	 * @returns total trip days
	 */
	INDRA_getBfmFlightDatesIntervalForPNRSummary(
		pnrSummary: INDRABookedTripSummary
	): number {
		let returnValue;
		if (!pnrSummary.origin) {
			returnValue = 0;
		}
		returnValue = this.dateService.INDRA_computeDifferenceOfDatesWithoutTimeInDays(pnrSummary.departureDate.toString(), pnrSummary.arrivalDate.toString());

		return returnValue;
	}

	/**
	 * Get text formated of passengers by type
	 * @param passengersByType list of passengers by type
	 * @returns text formated of passengers by type
	 */
	INDRA_getBfmPassengersByTypeForPNRSummary(
		pnrSummary: INDRABookedTripSummary
	): string {
		const passengersByType = [];

		if (pnrSummary.totalAdults > 1) {
			passengersByType.push(pnrSummary.totalAdults + ' ' + this.INDRA_getTranslation('bookingDetails.label.adults'));
		} else if (pnrSummary.totalAdults === 1) {
			passengersByType.push(pnrSummary.totalAdults + ' ' + this.INDRA_getTranslation('bookingDetails.label.adult'));
		}

		if (pnrSummary.totalChilds > 1) {
			passengersByType.push(pnrSummary.totalChilds + ' ' + this.INDRA_getTranslation('bookingDetails.label.children'));
		} else if (pnrSummary.totalChilds === 1) {
			passengersByType.push(pnrSummary.totalChilds + ' ' + this.INDRA_getTranslation('bookingDetails.label.child'));
		}

		if (pnrSummary.totalInfants > 1) {
			passengersByType.push(pnrSummary.totalInfants + ' ' + this.INDRA_getTranslation('bookingDetails.label.infants'));
		} else if (pnrSummary.totalInfants === 1) {
			passengersByType.push(pnrSummary.totalInfants + ' ' + this.INDRA_getTranslation('bookingDetails.label.infant'));
		}

		if (pnrSummary.totalYoungAdults > 1) {
			passengersByType.push(pnrSummary.totalYoungAdults + ' ' + this.INDRA_getTranslation('bookingDetails.label.youngAdults'));
		} else if (pnrSummary.totalYoungAdults === 1) {
			passengersByType.push(pnrSummary.totalYoungAdults + ' ' + this.INDRA_getTranslation('bookingDetails.label.youngAdult'));
		}

		return passengersByType.join(', ');
	}

	INDRA_getEquivalentDaysOfMins(mins: number) {
		const daysCount = Math.round(mins / 1440);
		return daysCount;
	}

	/**
	 * Get booking type text formated
	 * @param {String} booking_type to return
	 * @returns booking type text
	 */
	INDRA_getBookingType(booking_type: String): string {
		switch (booking_type) {
			case TRIP_TYPE_ROUND_TRIP_CODE:
				return this.INDRA_getTranslation('bookingDetails.bookingType.roundTrip');
			case TRIP_TYPE_ONE_WAY_CODE:
				return this.INDRA_getTranslation('bookingDetails.bookingType.oneWay');
			case TRIP_TYPE_MULTICITY_CODE:
				return this.INDRA_getTranslation('bookingDetails.bookingType.multicity');
			default:
				return;
		}
	}

	/**
	 * Get booking type icon
	 * @param {String} booking_type to return
	 * @returns booking type text
	 */
	INDRA_getBookingTypeIcon(booking_type: String): string {
		switch (booking_type) {
			case TRIP_TYPE_ROUND_TRIP_CODE:
				return 'icons.svg#plane_related--ida-volta';
			case TRIP_TYPE_ONE_WAY_CODE:
				return 'icons.svg#plane_related--outbound_flight';
			case TRIP_TYPE_MULTICITY_CODE:
				return 'icons.svg#plane_related--world_tracker';
			default:
				return 'icons.svg#plane_related--flights';
		}
	}

	/**
	 * Get booking dates text formated
	 * @param RetrievePnrData to get and return dates
	 * @returns booking dates text formated
	 */
	INDRA_getBfmFlightDates(pnr: INDRAPnrDataRecon): string {
		let returnValue;
		if (pnr.fare.listOutbound.length < 1) {
			return returnValue = '----';
		}
		const firstOutboundDate = pnr.fare.listOutbound[0].listSegment[0].departureDate;

		if (pnr.fare.tripType === TRIP_TYPE_ROUND_TRIP_CODE) {
			const lastInboundIndex =	pnr.fare.listInbound.length - 1;
			const lastInboundDate = pnr.fare.listInbound[lastInboundIndex].listSegment[0].departureDate;

			let sameDateFlag = this.dateService.INDRA_isSameDay(
				pnr.fare.listOutbound[0].listSegment[0].departureDate.toString(),
				lastInboundDate.toString());

			if (sameDateFlag) {
				// if departure and arrival date fall on the same day (IBETPIUNO-6313).
				return this.INDRA_formatDate(pnr.fare.listOutbound[0].listSegment[0].departureDate.toString());
			} else {
				let departureDate;
				if (
						// If departure and return on same month
						this.dateService.INDRA_getMonth(pnr.fare.listOutbound[0].listSegment[0].departureDate.toString()) ===
						this.dateService.INDRA_getMonth(lastInboundDate.toString())
					) {
						departureDate = this.dateService.INDRA_formatDate(pnr.fare.listOutbound[0].listSegment[0].departureDate.toString(), 'DD');
					} else {
						// If departure and return on same year and different month
						departureDate = this.dateService.INDRA_formatDate(pnr.fare.listOutbound[0].listSegment[0].departureDate.toString(), 'DD MMM');
					}
				return (
					departureDate +
					' - ' +
					this.INDRA_formatDate(lastInboundDate.toString())
				);
			}
		} else if (pnr.fare.tripType === TRIP_TYPE_MULTICITY_CODE) {
			const lastOutboundIndex =	pnr.fare.listOutbound.length - 1;
			const destinationDate = pnr.fare.listOutbound[lastOutboundIndex].listSegment[0].departureDate

			let sameDateFlag = this.dateService.INDRA_isSameDay(
				pnr.fare.listOutbound[0].listSegment[0].departureDate.toString(),
				destinationDate.toString());

			if (sameDateFlag) {
				// if departure and arrival date fall on the same day (IBETPIUNO-6313).
				return this.INDRA_formatDate(pnr.fare.listOutbound[0].listSegment[0].departureDate.toString());
			} else {
				let departureDate;
				if (
						// If departure and return on same month
						this.dateService.INDRA_getMonth(pnr.fare.listOutbound[0].listSegment[0].departureDate.toString()) ===
						this.dateService.INDRA_getMonth(destinationDate.toString())
					) {
						departureDate = this.dateService.INDRA_formatDate(pnr.fare.listOutbound[0].listSegment[0].departureDate.toString(), 'DD');
					} else {
						// If departure and return on same year and different month
						departureDate = this.dateService.INDRA_formatDate(pnr.fare.listOutbound[0].listSegment[0].departureDate.toString(),'DD MMM');
					}
					return (
						departureDate +
						' - ' +
						this.INDRA_formatDate(destinationDate.toString())
				);
			}

		} else {
			// Oneway
			returnValue = this.INDRA_formatDate(firstOutboundDate.toString());
		}
		return returnValue;
	}

	/**
	 * Get number of trip days
	 * @param RetrievePnrData to get number of trip days
	 * @returns total trip days
	 */
	INDRA_getBfmFlightDatesInterval(pnr: INDRAPnrDataRecon): number {
		let returnValue;
		if (pnr.fare.listOutbound.length < 1) {
			return returnValue = 0;
		}
		const departureDate = pnr.fare.listOutbound[0].listSegment[0].departureDate;

		let arrivalDate;
		if (pnr.fare.tripType === TRIP_TYPE_ROUND_TRIP_CODE) {
			// round trip
			const inboundLength = pnr.fare.listInbound.length;
			arrivalDate = pnr.fare.listInbound[inboundLength - 1].listSegment[0].departureDate;

		} else {
			// one way and multicity
			const outboundLength = pnr.fare.listOutbound.length;
			arrivalDate = pnr.fare.listOutbound[outboundLength - 1].listSegment[0].departureDate;
		}

		returnValue = this.dateService.INDRA_computeDifferenceOfDatesWithoutTimeInDays(departureDate.toString(), arrivalDate.toString());
		return returnValue;
	}

	/**
	 * Get text formated of passengers by type
	 * @param passengers list of passengers by type
	 * @returns text formated of passengers by type
	 */
	INDRA_getBfmPassengersByType(pnr: INDRAPnrDataRecon): string {
		const passengersByType = [];

		let adultPassengerSummary = "";
		let youngAdultPassengerSummary = "";
		let childPassengerSummary = "";
		let infantPassengerSummary = "";

		pnr.infoPax.listPaxNum.forEach(pax => {
			switch (pax.paxType) {
				case PAX_TYPE_ADULT_CODE:
					if (pax.paxNum > 1) {
						adultPassengerSummary = pax.paxNum + ' ' + this.INDRA_getTranslation('bookingDetails.label.adults');
					} else {
						adultPassengerSummary = pax.paxNum + ' ' + this.INDRA_getTranslation('bookingDetails.label.adult');
					}
					break;
				case PAX_TYPE_CHILD_CODE:
					if (pax.paxNum > 1) {
						childPassengerSummary = pax.paxNum + ' ' + this.INDRA_getTranslation('bookingDetails.label.children');
					} else {
						childPassengerSummary = pax.paxNum + ' ' + this.INDRA_getTranslation('bookingDetails.label.child');
					}
					break;
				case PAX_TYPE_INFANT_CODE:
					if (pax.paxNum > 1) {
						infantPassengerSummary = pax.paxNum +  ' ' + this.INDRA_getTranslation('bookingDetails.label.infants');
					} else {
						infantPassengerSummary = pax.paxNum + ' ' + this.INDRA_getTranslation('bookingDetails.label.infant');
					}
					break;
				case PAX_TYPE_YOUNG_ADULT_CODE:
					if (pax.paxNum > 1) {
						youngAdultPassengerSummary = pax.paxNum + ' ' + this.INDRA_getTranslation('bookingDetails.label.youngAdults');
					} else {
						youngAdultPassengerSummary = pax.paxNum + ' ' + this.INDRA_getTranslation('bookingDetails.label.youngAdult');
					}
					break;
			}
		});

		if (adultPassengerSummary) {
			passengersByType.push(adultPassengerSummary);
		}
		if (youngAdultPassengerSummary) {
			passengersByType.push(youngAdultPassengerSummary);
		}
		if (childPassengerSummary) {
			passengersByType.push(childPassengerSummary);
		}
		if (infantPassengerSummary) {
			passengersByType.push(infantPassengerSummary);
		}

		return passengersByType.join(', ');
	}

	private INDRA_getTranslation(key: string) : string {
		return this.ibeAdminService.getTranslationValue(key);
	}

}
